import {
  CountryAutocompleteField,
  Form,
  type InferType,
  MaterialDemandAutocomplete,
  MaterialGradeAutocomplete,
  MultiAccountManagerAutocompleteField,
  MultiMaterialFormAutocompleteField,
  MultiUnmatchedDemandSignalAutocomplete,
  PercentageField,
  RegionAutocompleteField,
  TradeCompanyAutocomplete,
  useForm,
  useWatch,
  yupResolver,
} from 'form';
import { Box, Button, Grid, Typography } from 'ui';

import { tradingDashboardFiltersFormSchema } from '../../components/DemandSignalForm/schema';
import { DEFAULT_TARGET_MARGIN } from '../../constants';
import { buildDemandSignalLabel } from '../../utils/buildDemandSignalLabel/buildDemandSignalLabel';

export type TradingDashboardFormValuesType = InferType<typeof tradingDashboardFiltersFormSchema>;

interface DemandSignalFormProps {
  onFiltersSubmit: (formValues: TradingDashboardFormValuesType) => void;
  isLoading: boolean;
}

export const DemandSignalForm = ({ onFiltersSubmit, isLoading }: DemandSignalFormProps) => {
  const form = useForm<TradingDashboardFormValuesType>({
    defaultValues: {
      target_margin: DEFAULT_TARGET_MARGIN,
    },
    resolver: yupResolver(tradingDashboardFiltersFormSchema),
  });

  const { resetField } = form;
  const [selectedMaterialId, selectedMaterialForms, selectedDemandSignalCountry] = useWatch({
    control: form.control,
    name: ['material', 'material_forms', 'signals_country'],
  });

  const selectedMaterialFormIds =
    selectedMaterialForms?.map((mf) => mf.id).filter((id): id is number => id !== undefined) ?? [];

  return (
    <Form {...form} onSubmit={onFiltersSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography fontWeight="bold">Demand side</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <MaterialDemandAutocomplete
                label="Filter by material type"
                name="material"
                onSelectOption={() => resetField('demand_signals')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MultiMaterialFormAutocompleteField label="Filter by material form" name="material_forms" />
            </Grid>
            <Grid item xs={12} md={4}>
              <CountryAutocompleteField name="signals_country" label="Filter signals by country" />
            </Grid>
            <Grid item xs={12}>
              <MultiUnmatchedDemandSignalAutocomplete
                label="Filter by demand signals"
                name="demand_signals"
                materialId={selectedMaterialId}
                materialFormIds={selectedMaterialFormIds ?? []}
                country={selectedDemandSignalCountry}
                getOptionLabel={buildDemandSignalLabel}
                withSelectAllOption
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography fontWeight="bold">Supply side</Typography>
            </Grid>
            <Grid item xs={12}>
              <MultiAccountManagerAutocompleteField
                tradeCompanyType="SELLER"
                label="Filter materials by seller AM"
                name="account_managers"
              />
            </Grid>
            <Grid item xs={12}>
              <CountryAutocompleteField name="site_material_country" label="Filter materials by country" />
            </Grid>
            <Grid item xs={12}>
              <RegionAutocompleteField name="site_material_region" label="Filter materials by region" />
            </Grid>
            <Grid item xs={12}>
              <TradeCompanyAutocomplete name="seller_id" label="Filter materials by seller" onlySellers={true} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography fontWeight="bold">Trading Preferences</Typography>
            </Grid>
            <Grid item xs={12}>
              <PercentageField name="target_margin" label="Default NTR %" />
            </Grid>
            <Grid item xs={12}>
              <CountryAutocompleteField name="exclude_country" label="Exclude materials from country" />
            </Grid>
            <Grid item xs={12}>
              <MaterialGradeAutocomplete label="Minimum grade" name="min_grade" />
            </Grid>
            <Grid item xs={12}>
              <MaterialGradeAutocomplete label="Maximum grade" name="max_grade" />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <Button type={'submit'} loading={isLoading}>
              Search
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Form>
  );
};
